import React, { useState } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { useTheme,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Collapse,
} from "@mui/material";
import { LAYOUT_HEIGHT } from "state/constants";


const CommonTableau = ({ 
    data, 
    mode = "average", 
    isAverage = false, 
    disableExpand = false 
}) => {
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const [openCell, setOpenCell] = useState(null);
    const [blinkCell, setBlinkCell] = useState(null);

    const handleClick = (cellKey) => {
        setOpenCell(openCell === cellKey ? null : cellKey);
        setBlinkCell(cellKey);
        setTimeout(() => setBlinkCell(null), 600);
    };

    if (!data || data.length === 0) {
        return (
            <Box p={1} sx={{ height: '100%', width: '100%' }}>
                <Typography>Nothing to display</Typography>
            </Box>
        );
    }

    const parsed = data.map((d) => {
        const [field1, field2] = d.key.split("|");
        return {
            field1,
            field2,
            count:      d.count      ?? 0,
            avgValue:   d.avgValue   ?? 0,
            totalValue: d.totalValue ?? 0,
            totalFlt:   d.totalFlt   ?? 0,
            totalBH:    d.totalBH    ?? 0,
            rawBH:      d.rawBH ?? d.totalBH ?? 0,
            totalDays:  d.totalDays  ?? 0,
            flights:    d.flights    || [],
        };
    });

    const isValidDateString = (v) =>
        typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v) && !isNaN(Date.parse(v));

    const field1Values = [...new Set(parsed.map((p) => p.field1))].sort((a, b) => {
        if (isValidDateString(a) && isValidDateString(b)) return new Date(a) - new Date(b);
        return a.localeCompare(b);
    });

    const field2Values = [...new Set(parsed.map((p) => p.field2))].sort();

    const getCellValue = (found) => {
        if (!found) return "-";
        if (mode === "count")      return found.count || "-";
        if (mode === "blockHours") return found.totalBH > 0 ? found.totalBH.toFixed(1) : "-";
        return found.avgValue > 0 ? found.avgValue.toFixed(1) : "-";
    };

    const getSummary = (items) => {
        const valid = items.filter(Boolean);
        if (mode === "count") {
            return valid.reduce((s, v) => s + (v.count || 0), 0);
        }
        if (mode === "blockHours") {
            const days = valid.reduce((s, v) => s + (v.totalDays ?? 0), 0);
            const raw  = valid.reduce((s, v) => s + (v.rawBH ?? v.totalBH ?? 0), 0);
            return days > 0 ? parseFloat((raw / days).toFixed(2)) : parseFloat(raw.toFixed(1));
        }
        const sumFlt   = valid.reduce((s, v) => s + (v.totalFlt || 0), 0);
        const sumValue = valid.reduce((s, v) => s + (v.totalValue || 0), 0);
        return sumFlt > 0 ? parseFloat((sumValue / sumFlt).toFixed(1)) : null;
    };

    const fmtSummary = (v) => {
        if (v === null || v === undefined) return "-";
        if (mode === "count") return v || "-";
        return v > 0 ? v.toFixed(1) : "-";
    };

    const footerLabel = mode === "count"      ? "Total"
        : mode === "blockHours" ? (isAverage ? "Average" : "Total")
        : "Average";

    const extraCol = mode === "blockHours" ? { label: "BH",    render: (f) => f.bh?.toFixed(2) ?? "-" }
        : mode === "average"    ? { label: "Value",  render: (f) => f.val ?? "-" }
        : null;

    return (
        <TableContainer component={Paper} sx={{ maxHeight: LAYOUT_HEIGHT }}>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: "bold", minWidth: "90px" }}>Field</TableCell>
                        {field1Values.map((field1) => (
                            <TableCell key={field1} align="center" sx={{ fontWeight: "bold" }}>
                                {isValidDateString(field1) ? format(new Date(field1), "dd.MM.yy") : field1}
                            </TableCell>
                        ))}
                        <TableCell align="center" sx={{ fontWeight: "bold" }}>{footerLabel}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {field2Values.map((field2) => {
                        const rowItems = field1Values.map((field1) =>
                            parsed.find((p) => p.field1 === field1 && p.field2 === field2)
                        );
                        const rowSummary = getSummary(rowItems);

                        return (
                            <React.Fragment key={field2}>
                                <TableRow>
                                    <TableCell component="th" scope="row">{field2 || ""}</TableCell>
                                    {field1Values.map((field1) => {
                                        const found = parsed.find(
                                            (p) => p.field1 === field1 && p.field2 === field2
                                        );
                                        const cellKey = `${field1}-${field2}`;
                                        return (
                                            <TableCell
                                                key={cellKey}
                                                align="center"
                                                onClick={() => !disableExpand && handleClick(cellKey)}
                                                sx={{
                                                    cursor: disableExpand ? "default" : "pointer",
                                                    backgroundColor: openCell === cellKey
                                                        ? theme.palette.action.selected
                                                        : "inherit",
                                                    fontWeight: openCell === cellKey ? "bold" : "normal",
                                                    ...(blinkCell === cellKey && {
                                                        animation: "blink 0.6s ease-in-out",
                                                    }),
                                                    "@keyframes blink": {
                                                        "0%": { backgroundColor: theme.palette.primary.light, color: theme.palette.text.primary },
                                                        "50%": { backgroundColor: theme.palette.action.hover },
                                                        "100%": { backgroundColor: "inherit" },
                                                    },
                                                }}
                                            >
                                                {getCellValue(found)}
                                            </TableCell>
                                        );
                                    })}
                                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                        {fmtSummary(rowSummary)}
                                    </TableCell>
                                </TableRow>

                                {!disableExpand && field1Values.map((field1) => {
                                    const found = parsed.find(
                                        (p) => p.field1 === field1 && p.field2 === field2
                                    );
                                    const cellKey = `${field1}-${field2}`;
                                    return (
                                        openCell === cellKey && found && found.flights.length > 0 && (
                                            <TableRow key={`${cellKey}-details`}>
                                                <TableCell colSpan={field1Values.length + 2} sx={{ p: 0 }}>
                                                    <Collapse in={openCell === cellKey} timeout="auto" unmountOnExit>
                                                        <Box marginLeft={2}>
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow sx={{ "& .MuiTableCell-root": { color: theme.palette.primary.dark } }}>
                                                                        <TableCell>Date</TableCell>
                                                                        <TableCell>FlightNo</TableCell>
                                                                        <TableCell>Sector</TableCell>
                                                                        <TableCell>Std</TableCell>
                                                                        <TableCell>Sta</TableCell>
                                                                        <TableCell>Etd</TableCell>
                                                                        <TableCell>Eta</TableCell>
                                                                        <TableCell>Atd</TableCell>
                                                                        <TableCell>Ata</TableCell>
                                                                        {extraCol && <TableCell>{extraCol.label}</TableCell>}
                                                                        <TableCell>Status</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {found.flights.map((f, idx) => {
                                                                        const isLocal = timeMode !== "UTC";
                                                                        const flightDate = isLocal ? f.flightDateLocalBase : f.flightDate;
                                                                        return (
                                                                            <TableRow key={idx}>
                                                                                <TableCell>
                                                                                    {flightDate ? format(new Date(flightDate), "dd.MM.yy") : "-"}
                                                                                </TableCell>
                                                                                <TableCell>{f.flightNo}</TableCell>
                                                                                <TableCell>{f.sector}</TableCell>
                                                                                <TableCell>{isLocal ? f.stdLocalBase : f.std}</TableCell>
                                                                                <TableCell>{isLocal ? f.staLocalBase : f.sta}</TableCell>
                                                                                <TableCell>{isLocal ? f.etdLocalBase : f.etd}</TableCell>
                                                                                <TableCell>{isLocal ? f.etaLocalBase : f.eta}</TableCell>
                                                                                <TableCell>{isLocal ? f.atdLocalBase : f.atd}</TableCell>
                                                                                <TableCell>{isLocal ? f.ataLocalBase : f.ata}</TableCell>
                                                                                {extraCol && <TableCell>{extraCol.render(f)}</TableCell>}
                                                                                <TableCell>{f.flightStatus}</TableCell>
                                                                            </TableRow>
                                                                        );
                                                                    })}
                                                                </TableBody>
                                                            </Table>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
                {(() => {
                    const grandSummary = getSummary(parsed);
                    return (
                        <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>{footerLabel}</TableCell>
                            {field1Values.map((field1) => (
                                <TableCell key={`summary-${field1}`} align="center" sx={{ fontWeight: "bold" }}>
                                    {fmtSummary(getSummary(parsed.filter((p) => p.field1 === field1)))}
                                </TableCell>
                            ))}
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                {fmtSummary(grandSummary)}
                            </TableCell>
                        </TableRow>
                    );
                })()}
            </Table>
        </TableContainer>
    );
};

export default CommonTableau;
